import React, { FC, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Stack,
  Icon,
  Chip,
  InputAdornment,
  Tooltip,
  alpha,
  useTheme,
  Button,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  defaultDropAnimation,
  UniqueIdentifier,
  Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  SortableData,
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Instruction {
  content: string;
  preparation_time: number;
  preparation_time_unit: string;
  cooking_time: number;
  cooking_time_unit: string;
  temperature: number;
  temperature_unit: "C" | "F";
}

interface InstructionEditorProps {
  instructions: Instruction[];
  onChange: (instructions: Instruction[]) => void;
}

interface SortableInstructionProps {
  instruction: Instruction;
  index: number;
  previewMode: boolean;
  editingIndex: number | null;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof Instruction, value: any) => void;
  onToggleTemperature: (index: number) => void;
}

const animateLayoutChanges: AnimateLayoutChanges = (args) => {
  const { isSorting, isDragging } = args;

  if (isSorting || isDragging) {
    return true;
  }

  return defaultAnimateLayoutChanges(args);
};

const SortableInstruction: FC<SortableInstructionProps> = ({
  instruction,
  index,
  previewMode,
  editingIndex,
  onEdit,
  onRemove,
  onUpdate,
  onToggleTemperature,
}) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: index.toString(),
    data: {
      type: "instruction",
      instruction,
      index,
    },
    animateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: [
      transition,
      theme.transitions.create(["transform", "opacity", "box-shadow"], {
        duration: isSorting ? 250 : 0,
      }),
    ]
      .filter(Boolean)
      .join(", "),
    opacity: isDragging ? 0 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <Card
        variant="outlined"
        sx={{
          position: "relative",
          bgcolor: isDragging
            ? alpha(theme.palette.primary.main, 0.04)
            : "background.paper",
          borderColor: isDragging ? "#0891B2" : "divider",
          transform: isDragging ? "scale(1.02)" : "none",
          transition: theme.transitions.create(
            ["background-color", "border-color", "transform", "box-shadow"],
            {
              duration: 200,
            }
          ),
          "&:hover": {
            borderColor: "#0891B2",
            bgcolor: alpha("#0891B2", 0.04),
            "& .drag-handle": {
              opacity: 1,
              color: "#0891B2",
            },
          },
          "& .drag-handle": {
            opacity: isDragging ? 1 : 0,
            transition: theme.transitions.create(["opacity", "color"], {
              duration: 200,
            }),
          },
          boxShadow: isDragging
            ? `0 16px 32px ${alpha("#0891B2", 0.2)}`
            : "none",
          cursor: isDragging ? "grabbing" : "default",
          zIndex: isDragging ? theme.zIndex.drawer : 1,
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <IconButton
                {...attributes}
                {...listeners}
                size="small"
                className="drag-handle"
                sx={{
                  mt: 0.5,
                  color: "#64748B",
                  "&:hover": {
                    color: "#0891B2",
                    bgcolor: alpha("#0891B2", 0.04),
                  },
                }}
              >
                <Icon>drag_indicator</Icon>
              </IconButton>

              <Box sx={{ flex: 1 }}>
                {editingIndex === index ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={instruction.content}
                    onChange={(e) => onUpdate(index, "content", e.target.value)}
                    placeholder="Enter instruction step..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "background.paper",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.02),
                        },
                        "&.Mui-focused": {
                          bgcolor: "background.paper",
                          boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                        },
                      },
                    }}
                  />
                ) : (
                  <Box onClick={() => onEdit(index)}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {instruction.content}
                    </ReactMarkdown>
                  </Box>
                )}

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 2 }}
                  alignItems="center"
                >
                  <TextField
                    size="small"
                    type="number"
                    label="Prep Time"
                    defaultValue={0}
                    value={instruction.preparation_time}
                    onChange={(e) => {
                      let value =
                        e.target.value === ""
                          ? 0
                          : Math.max(0, parseInt(e.target.value));
                      if (isNaN(value)) value = 0;
                      onUpdate(index, "preparation_time", value);
                    }}
                    onBlur={(e) => {
                      let value =
                        e.target.value === ""
                          ? 0
                          : Math.max(0, parseInt(e.target.value));
                      if (isNaN(value)) value = 0;
                      onUpdate(index, "preparation_time", value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {instruction.preparation_time_unit}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover": {
                          "& > fieldset": {
                            borderColor: "#0891B2",
                          },
                        },
                      },
                    }}
                  />

                  <TextField
                    size="small"
                    type="number"
                    label="Cook Time"
                    defaultValue={0}
                    value={instruction.cooking_time}
                    onChange={(e) => {
                      let value =
                        e.target.value === ""
                          ? 0
                          : Math.max(0, parseInt(e.target.value));
                      if (isNaN(value)) value = 0;
                      onUpdate(index, "cooking_time", value);
                    }}
                    onBlur={(e) => {
                      let value =
                        e.target.value === ""
                          ? 0
                          : Math.max(0, parseInt(e.target.value));
                      if (isNaN(value)) value = 0;
                      onUpdate(index, "cooking_time", value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {instruction.cooking_time_unit}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover": {
                          "& > fieldset": {
                            borderColor: "#0891B2",
                          },
                        },
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Temperature"
                      value={instruction.temperature || 0}
                      onChange={(e) => {
                        const value =
                          e.target.value === "" ? 0 : parseInt(e.target.value);
                        onUpdate(
                          index,
                          "temperature",
                          isNaN(value) ? 0 : value
                        );
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            °{instruction.temperature_unit || "C"}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover": {
                            "& > fieldset": {
                              borderColor: "#0891B2",
                            },
                          },
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onToggleTemperature(index)}
                      sx={{
                        minWidth: 48,
                        px: 1,
                        color: "#0891B2",
                        borderColor: alpha("#0891B2", 0.5),
                        "&:hover": {
                          borderColor: "#0891B2",
                          bgcolor: alpha("#0891B2", 0.04),
                        },
                      }}
                    >
                      °{instruction.temperature_unit === "F" ? "C" : "F"}
                    </Button>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(index)}
                  sx={{
                    color: "#64748B",
                    "&:hover": {
                      color: "#0891B2",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onRemove(index)}
                  sx={{
                    color: "#64748B",
                    "&:hover": {
                      color: "#DC2626",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export const InstructionEditor: FC<InstructionEditorProps> = ({
  instructions,
  onChange,
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddInstruction = () => {
    onChange([
      ...instructions,
      {
        content: "",
        preparation_time: 0,
        preparation_time_unit: "min",
        cooking_time: 0,
        cooking_time_unit: "min",
        temperature: 0,
        temperature_unit: "C",
      },
    ]);
    setEditingIndex(instructions.length);
  };

  const handleUpdateInstruction = (
    index: number,
    field: keyof Instruction,
    value: any
  ) => {
    const newInstructions = [...instructions];
    const updatedInstruction = {
      ...newInstructions[index],
      [field]: value,
    };
    newInstructions[index] = updatedInstruction;
    onChange(newInstructions);
  };

  const handleToggleTemperature = (index: number) => {
    const newInstructions = [...instructions];
    const currentInstruction = newInstructions[index];
    const currentTemp = currentInstruction.temperature || 0;
    const currentUnit = currentInstruction.temperature_unit || "C";

    // Calculate new values
    const newUnit = currentUnit === "C" ? "F" : ("C" as "C" | "F");
    const newTemp =
      currentUnit === "C"
        ? Math.round(((currentTemp * 9) / 5 + 32) * 10) / 10 // C to F
        : Math.round((((currentTemp - 32) * 5) / 9) * 10) / 10; // F to C

    // Create new instruction with both updates
    const updatedInstruction: Instruction = {
      ...currentInstruction,
      temperature: newTemp,
      temperature_unit: newUnit,
    };

    // Update the instruction in the array
    newInstructions[index] = updatedInstruction;

    // Update parent state with the new array
    onChange(newInstructions);
  };

  const handleRemoveInstruction = (index: number) => {
    const newInstructions = [...instructions];
    newInstructions.splice(index, 1);
    onChange(newInstructions);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);

      onChange(arrayMove(instructions, oldIndex, newIndex));
    }

    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              color: "#0891B2",
            }}
          >
            Instructions
          </Typography>
          <Button
            startIcon={<Icon>add</Icon>}
            onClick={handleAddInstruction}
            variant="contained"
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Step
          </Button>
        </Box>

        <SortableContext
          items={instructions.map((_, i) => i.toString())}
          strategy={verticalListSortingStrategy}
        >
          {instructions.map((instruction, index) => (
            <SortableInstruction
              key={index}
              instruction={instruction}
              index={index}
              previewMode={previewMode}
              editingIndex={editingIndex}
              onEdit={setEditingIndex}
              onRemove={handleRemoveInstruction}
              onUpdate={handleUpdateInstruction}
              onToggleTemperature={handleToggleTemperature}
            />
          ))}
        </SortableContext>

        {instructions.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: alpha("#0891B2", 0.04),
              borderRadius: 2,
              border: "2px dashed",
              borderColor: alpha("#0891B2", 0.2),
            }}
          >
            <Icon
              sx={{
                fontSize: 48,
                color: alpha("#0891B2", 0.3),
                mb: 2,
              }}
            >
              format_list_numbered
            </Icon>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: "#0891B2",
              }}
            >
              No Instructions Added
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontFamily: "Montserrat",
              }}
            >
              Start adding step-by-step instructions for your recipe
            </Typography>
          </Box>
        )}
      </Box>

      {createPortal(
        <DragOverlay dropAnimation={defaultDropAnimation}>
          {activeId !== null && (
            <SortableInstruction
              instruction={instructions[Number(activeId)]}
              index={Number(activeId)}
              previewMode={previewMode}
              editingIndex={editingIndex}
              onEdit={setEditingIndex}
              onRemove={handleRemoveInstruction}
              onUpdate={handleUpdateInstruction}
              onToggleTemperature={handleToggleTemperature}
            />
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};
